import { Form } from '@ana/shared'
import { format } from 'date-fns'
import * as DiffType from 'entities/DiffType'
import * as Disease from 'entities/Disease'
import dateHelpers from 'helpers/date'
import diffArrays from 'helpers/diffArray'
import _get from 'lodash/fp/get'
import _identity from 'lodash/identity'
import _uniq from 'lodash/uniq'

import { Anamnesis } from '../@types/Anamnesis'
import DetailedDiff from '../@types/DetailedDiff'
import DiseaseType from '../@types/DiseaseType'

const fromForm = (form: Partial<Form>): Anamnesis => {
  return {
    lifeStyle: {
      alcoholConsumption: form.alcoholConsumption ?? '',
      drugConsumption: form.drugConsumption ?? '',
      isPregnant: form.isPregnant ?? false,
      cigaretteConsumption: form.cigaretteConsumption ?? '',
      occupation: form.occupation ?? null,
      pregnantMonths: form.pregnantMonths ?? null,
    },
    insurance: {
      id: '',
      holderFirstName: form.holderFirstName ?? null,
      holderLastName: form.holderLastName ?? null,
      holderBirthday: form.holderBirthday ?? null,
      holderCity: form.holderCity ?? null,
      holderStreet: form.holderStreet ?? null,
      hasCareLevel: form.hasCareLevel ?? null,
      careLevelLimitedInTime: form.careLevelLimitedInTime ?? null,
      holderZipcode: form.holderZipcode ?? null,
      company: form.insuranceCompany ?? '',
      type: form.type ?? '',
      companyId: form.insuranceCompany ?? null,
      hasDentalInsurance: form.hasDentalInsurance ?? null,
      hasFamilyInsurance: form.hasFamilyInsurance ?? null,
      dentalInsuranceCompany: form.dentalInsuranceCompany ?? null,
      livesOnDifferentAddress: form.livesOnDifferentAddress ?? false,
      dentalInsuranceCompanyId: form.dentalInsuranceCompanyId ?? null,
      hasCivilServiceAllowance: form.hasCivilServiceAllowance ?? null,
    },
    person: {
      firstName: form.firstName ?? '',
      lastName: form.lastName ?? '',
      address: {
        city: form.city ?? '',
        street: form.street ?? '',
        zipCode: form.zipCode ?? '',
      },
      email: form.email || null,
      birthday: form.birthday ?? '',
      phone: form.phone ?? '',
      gender: form.gender ?? '',
      placeOfBirth: form.placeOfBirth ?? '',
      alternativePhone: form.alternativePhone ?? null,
      contactPersonLastName: form.contactPersonLastName ?? null,
      contactPersonFirstName: form.contactPersonFirstName ?? null,
      contactPersonRelation: form.contactPersonRelation ?? null,
      filedForDifferentPerson: form.filedForDifferentPerson ?? null,
      id: '',
      referenceId: '',
    },
    hasAllergies: form.hasAllergies ?? 'UNKNOWN',
    diseases: form.diseases || [],
    wishesAndExpectations: form.wishesAndExpectations || [],
    medications: form.medications || [],
    referral: form.referral ?? null,
    allergyPass: form.allergyPass ?? false,
    bodyHeightInCm: form.bodyHeightInCm ?? 0,
    bodyWeightInKg: form.bodyWeightInKg ?? 0,
    comments: {},
  }
}

const toForm = (anamnesis: Anamnesis): Partial<Form> => {
  const {
    lifeStyle = {} as Anamnesis['lifeStyle'],
    insurance = {} as Anamnesis['insurance'],
    person: { address, ...person } = {} as Anamnesis['person'],
    comments: _,
    ...rest
  } = anamnesis

  const parseBirthday = (value: string) => {
    if (dateHelpers.BDAY_REGEX.test(value)) {
      return value
    }

    return format(new Date(person.birthday), 'dd.MM.yyyy')
  }

  return {
    ...lifeStyle,
    ...person,
    ...address,
    ...rest,
    filedForDifferentPerson: person.filedForDifferentPerson ?? false,
    contactPersonLastName: person.contactPersonLastName ?? undefined,
    contactPersonFirstName: person.contactPersonFirstName ?? undefined,
    contactPersonRelation: person.contactPersonRelation ?? undefined,
    email: person.email ?? undefined,
    alternativePhone: person.alternativePhone ?? undefined,
    occupation: lifeStyle.occupation ?? undefined,
    referral: rest.referral ?? undefined,
    birthday: person?.birthday ? parseBirthday(person.birthday) : '',
    alcoholConsumption: lifeStyle.alcoholConsumption ?? undefined,
    drugConsumption: lifeStyle.drugConsumption ?? undefined,
    isPregnant: lifeStyle.isPregnant ?? false,
    cigaretteConsumption: lifeStyle.cigaretteConsumption ?? undefined,
    pregnantMonths: lifeStyle.pregnantMonths ?? undefined,
    // eslint-disable-next-line no-constant-binary-expression
    hasFamilyInsurance: Boolean(insurance?.holderFirstName) ?? false,

    // insurance
    insuranceCompany: insurance?.company,
    insuranceCompanyId: insurance?.companyId || undefined,
    dentalInsuranceCompany: insurance?.dentalInsuranceCompany ?? undefined,
    dentalInsuranceCompanyId: insurance?.dentalInsuranceCompanyId ?? undefined,
    hasCivilServiceAllowance: insurance?.hasCivilServiceAllowance ?? false,
    type: insurance?.type ?? undefined,
    hasCareLevel: insurance?.hasCareLevel ?? undefined,
    careLevelLimitedInTime: insurance?.careLevelLimitedInTime ?? undefined,
    hasDentalInsurance: Boolean(insurance?.dentalInsuranceCompany || insurance?.dentalInsuranceCompanyId),
    entitledToIntegrationAssistance: insurance?.entitledToIntegrationAssistance ?? undefined,
    integrationAssistanceBasedOnType: insurance?.integrationAssistanceBasedOnType ?? undefined,
    holderFirstName: insurance?.holderFirstName ?? undefined,
    holderLastName: insurance?.holderLastName ?? undefined,
    holderBirthday: insurance?.holderBirthday ?? undefined,
    holderCity: insurance?.holderCity ?? undefined,
    holderStreet: insurance?.holderStreet ?? undefined,
    holderZipcode: insurance?.holderZipcode ?? undefined,
    livesOnDifferentAddress: insurance?.livesOnDifferentAddress ?? false,

    hasAllergies:
      rest.hasAllergies ??
      (rest.diseases.some(
        (disease) => disease.type === DiseaseType.OTHER_ALLERGIES || disease.type === DiseaseType.MEDICATION_ALLERGIES
      )
        ? 'YES'
        : 'NO'),
    wishesAndExpectations: (rest.wishesAndExpectations || []).map((wish) => ({
      ...wish,
      flag: wish.score ? undefined : wish.flag,
    })),
    medications: rest.medications ?? [],
  }
}

const getDiff = (originalAnamnesis: Anamnesis, currentAnamnesis: Anamnesis): DetailedDiff => {
  const originalForm = toForm(originalAnamnesis)
  const currentForm = toForm(currentAnamnesis)

  const diseasesDiff = Disease.getDiff(originalForm.diseases || [], currentForm.diseases || [])

  const wishesDiff = diffArrays(
    originalForm.wishesAndExpectations || [],
    currentForm.wishesAndExpectations || [],
    _get('key')
  )

  // Get all the keys from both versions to make sure that we don't skip any of them.
  // If we use only the keys from `originalForm`, we might not calculate the diff
  // for newly added fields in the current version
  const allFormKeys = _uniq([...Object.keys(originalForm), ...Object.keys(currentForm)])

  const simpleFormDiff = Object.fromEntries(
    allFormKeys.map((key) => {
      const diffType = DiffType.getType(originalForm[key as keyof Form], currentForm[key as keyof Form])

      return [key, diffType]
    })
  )

  return {
    ...simpleFormDiff,
    ...wishesDiff,
    diseases: diseasesDiff,
    medications: diffArrays(originalForm.medications || [], currentForm.medications || [], _identity),
  } as DetailedDiff
}

export default {
  toForm,
  fromForm,
  getDiff,
}
